import type { LearningDuration } from '../../types/learning';

type Clock = () => number;

/**
 * Tracks wall-clock, active, and paused time separately.
 * It intentionally does not track pointer movement or keystrokes.
 */
export class ActivityTimer {
  private readonly startedAt: number;
  private activeStartedAt: number | null;
  private pausedStartedAt: number | null = null;
  private activeAccumulatedMs = 0;
  private pausedAccumulatedMs = 0;

  constructor(private readonly now: Clock = () => Date.now()) {
    this.startedAt = this.now();
    this.activeStartedAt = this.startedAt;
  }

  pause() {
    if (this.pausedStartedAt !== null) return;
    const current = this.now();
    if (this.activeStartedAt !== null) {
      this.activeAccumulatedMs += Math.max(0, current - this.activeStartedAt);
      this.activeStartedAt = null;
    }
    this.pausedStartedAt = current;
  }

  resume() {
    if (this.pausedStartedAt === null) return;
    const current = this.now();
    this.pausedAccumulatedMs += Math.max(0, current - this.pausedStartedAt);
    this.pausedStartedAt = null;
    this.activeStartedAt = current;
  }

  snapshot(): LearningDuration {
    const current = this.now();
    const activeDurationMs = this.activeAccumulatedMs +
      (this.activeStartedAt === null ? 0 : Math.max(0, current - this.activeStartedAt));
    const pausedDurationMs = this.pausedAccumulatedMs +
      (this.pausedStartedAt === null ? 0 : Math.max(0, current - this.pausedStartedAt));

    return {
      elapsedDurationMs: Math.max(0, current - this.startedAt),
      activeDurationMs,
      pausedDurationMs,
    };
  }
}
